import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ScanLine, CheckCircle2, XCircle, Eye, Sparkles } from "lucide-react";
import client, { API } from "@/lib/api";
import { StatusBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

export default function DocumentsAdmin() {
  const [docs, setDocs] = useState([]);
  const [ocr, setOcr] = useState(null);
  const [running, setRunning] = useState(null);
  const [status, setStatus] = useState("all");

  const load = useCallback(() => {
    client.get("/records/documents", { params: { limit: 100, status } })
      .then((r) => setDocs(r.data.items)).catch(() => {});
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const runOcr = async (d) => {
    setRunning(d.id);
    toast.info("Running AI OCR extraction...");
    try {
      const { data } = await client.post(`/documents/${d.id}/ocr`);
      setOcr({ doc: d, result: data });
    } catch (e) {
      toast.error(e.response?.data?.detail || "OCR failed");
    } finally { setRunning(null); }
  };

  const verify = async (d, newStatus) => {
    try { await client.post(`/documents/${d.id}/verify`, { status: newStatus }); toast.success(`Marked ${newStatus}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#0A192F]">Documents</h1>
          <p className="text-sm text-slate-400">Verify documents and run AI/OCR extraction</p>
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="doc-status-filter" className="h-9 px-3 rounded-lg border border-slate-200 bg-white text-sm">
          {["all", "Uploaded", "Under Review", "Verified", "Rejected"].map((s) => <option key={s} value={s}>{s === "all" ? "All Status" : s}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr><th className="text-left px-4 py-3">Candidate</th><th className="text-left px-4 py-3">Type</th><th className="text-left px-4 py-3">Doc No.</th><th className="text-left px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {docs.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No documents.</td></tr>}
              {docs.map((d) => (
                <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-[#0A192F]">{d.candidate_name}</td>
                  <td className="px-4 py-3">{d.doc_type}</td>
                  <td className="px-4 py-3 text-slate-500">{d.document_number || "—"}</td>
                  <td className="px-4 py-3"><StatusBadge value={d.status} /></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap space-x-1">
                    {d.file_id && (
                      <>
                        <a href={`${API}/documents/${d.id}/download`} target="_blank" rel="noreferrer" className="inline-block"><Button size="sm" variant="outline"><Eye className="h-4 w-4" /></Button></a>
                        <Button size="sm" variant="outline" disabled={running === d.id} onClick={() => runOcr(d)} data-testid={`ocr-${d.id}`}>
                          <ScanLine className="h-4 w-4 mr-1" /> {running === d.id ? "..." : "OCR"}
                        </Button>
                      </>
                    )}
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => verify(d, "Verified")} data-testid={`verify-${d.id}`}><CheckCircle2 className="h-4 w-4" /></Button>
                    <Button size="sm" variant="destructive" onClick={() => verify(d, "Rejected")} data-testid={`reject-${d.id}`}><XCircle className="h-4 w-4" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {ocr && (
        <Dialog open onOpenChange={() => setOcr(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-[#0088FF]" /> AI Extraction — {ocr.doc.doc_type}</DialogTitle></DialogHeader>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm text-slate-500">Confidence:</span>
              <StatusBadge value={ocr.result.confidence === "high" ? "Verified" : ocr.result.confidence === "medium" ? "Under Review" : "Rejected"} />
              {ocr.result.needs_review && <span className="text-xs text-amber-600">Human verification recommended</span>}
            </div>
            {ocr.result.error && <p className="text-sm text-rose-600 mb-2">{ocr.result.error}</p>}
            <div className="bg-slate-50 rounded-xl p-4 space-y-1.5 max-h-72 overflow-y-auto">
              {Object.entries(ocr.result.fields || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between text-sm border-b border-slate-100 py-1.5 last:border-0">
                  <span className="text-slate-500 capitalize">{k.replace(/_/g, " ")}</span>
                  <span className="font-medium text-[#0A192F] text-right">{v == null || v === "" ? "—" : String(v)}</span>
                </div>
              ))}
              {Object.keys(ocr.result.fields || {}).length === 0 && <div className="text-sm text-slate-400">No fields extracted.</div>}
            </div>
            <p className="text-xs text-slate-400 mt-2">OCR is AI-assisted. Please verify extracted values before relying on them.</p>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
